import { Effect, Layer, Option } from 'effect';
import { PDFDocument } from 'pdf-lib';

import {
  DocumentSource,
  makeParserDependencyError,
  PageSource,
  ParserHttp,
  ParserSecrets,
  ParserTracer,
  PdfTextLayer,
  parserTracerNoopService,
  type PageImage,
  type PageRegionClip,
  type ParseDocumentRef,
  type PdfTextLayerPage,
} from '../parser-host';
import { cropPdfPageToRegionEffect } from './pdf-crop';

export interface ParserHostMemoryOptions {
  readonly pageCount?: number;
  readonly pagePdfs?: ReadonlyMap<number, Uint8Array>;
  readonly pageImages?: ReadonlyMap<number, PageImage>;
  readonly textLayers?: ReadonlyMap<number, PdfTextLayerPage>;
  readonly secrets?: Readonly<Record<string, string | undefined>>;
  /** Storage-ref fixtures keyed `${provider}:${bucket}/${key}`. */
  readonly storage?: ReadonlyMap<string, Uint8Array>;
}

function pageSourceFailure(operation: string, code: string, cause?: unknown) {
  return makeParserDependencyError(
    { service: 'page-source', operation, code, retryable: false },
    cause,
  );
}

function countPages(
  document: ParseDocumentRef,
  configured: number | undefined,
  resolveBytes: (
    ref: ParseDocumentRef,
  ) => Effect.Effect<Uint8Array, ReturnType<typeof pageSourceFailure>>,
): Effect.Effect<number, ReturnType<typeof pageSourceFailure>> {
  if (document.pageCount !== undefined) return Effect.succeed(document.pageCount);
  if (configured !== undefined) return Effect.succeed(configured);
  return resolveBytes(document).pipe(
    Effect.flatMap((bytes) =>
      Effect.tryPromise({
        try: async () => (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(),
        catch: (cause) => pageSourceFailure('count-pages', 'invalid_pdf', cause),
      }),
    ),
  );
}

async function splitPdfBytes(bytes: Uint8Array, page: number): Promise<Uint8Array> {
  const source = await PDFDocument.load(bytes, { ignoreEncryption: true });
  if (!Number.isInteger(page) || page < 1 || page > source.getPageCount()) {
    throw new RangeError('page is outside the document');
  }
  const output = await PDFDocument.create();
  const [copied] = await output.copyPages(source, [page - 1]);
  output.addPage(copied);
  return output.save();
}

function splitPagePdf(
  document: ParseDocumentRef,
  page: number,
  resolveBytes: (
    ref: ParseDocumentRef,
  ) => Effect.Effect<Uint8Array, ReturnType<typeof pageSourceFailure>>,
): Effect.Effect<Uint8Array, ReturnType<typeof pageSourceFailure>> {
  return resolveBytes(document).pipe(
    Effect.flatMap((bytes) =>
      Effect.tryPromise({
        try: () => splitPdfBytes(bytes, page),
        catch: (cause) => pageSourceFailure('page-pdf', 'invalid_page', cause),
      }),
    ),
  );
}

/**
 * Deterministic fixture host. It intentionally omits ParserHttp, so offline
 * compatibility is enforced by the Effect requirement channel.
 */
export function parserHostMemory(
  options: ParserHostMemoryOptions = {},
): Layer.Layer<PageSource | PdfTextLayer | ParserSecrets | DocumentSource | ParserTracer> {
  const resolveBytes = (
    document: ParseDocumentRef,
  ): Effect.Effect<Uint8Array, ReturnType<typeof pageSourceFailure>> => {
    if (document.kind !== 'storage') return Effect.succeed(document.bytes);
    const fixture = options.storage?.get(`${document.provider}:${document.bucket}/${document.key}`);
    return fixture
      ? Effect.succeed(fixture.slice())
      : Effect.fail(pageSourceFailure('bytes', 'fixture_not_found'));
  };
  const pageSource = PageSource.of({
    countPages: (document) => countPages(document, options.pageCount, resolveBytes),
    pagePdf: (document, page, clip?: PageRegionClip) => {
      const fixture = options.pagePdfs?.get(page);
      if (fixture) return Effect.succeed(fixture.slice());
      return clip?.region === undefined
        ? splitPagePdf(document, page, resolveBytes)
        : splitPagePdf(document, page, resolveBytes).pipe(
            Effect.flatMap((bytes) => cropPdfPageToRegionEffect(bytes, clip.region!)),
          );
    },
    // Region clips are a recorded-double surface here: the fixture is returned
    // unchanged, mirroring the no-I/O contract of the memory pack.
    pageImage: (_document, page, _raster, _clip?: PageRegionClip) => {
      const fixture = options.pageImages?.get(page);
      return fixture
        ? Effect.succeed({ ...fixture, bytes: fixture.bytes.slice() })
        : Effect.fail(pageSourceFailure('page-image', 'fixture_not_found'));
    },
  });
  const textLayer = PdfTextLayer.of({
    extractPage: (_document, page) => {
      const fixture = options.textLayers?.get(page);
      return fixture
        ? Effect.succeed({
            ...fixture,
            items: fixture.items.map((item) => ({ ...item })),
          })
        : Effect.fail(
            makeParserDependencyError({
              service: 'text-layer',
              operation: 'extract-page',
              code: 'fixture_not_found',
              retryable: false,
            }),
          );
    },
  });
  const secrets = ParserSecrets.of({
    get: (name) => Effect.succeed(Option.fromNullishOr(options.secrets?.[name])),
  });
  const documentSource = DocumentSource.of({
    bytes: (document) =>
      resolveBytes(document).pipe(
        Effect.mapError((cause) =>
          makeParserDependencyError(
            {
              service: 'document-source',
              operation: 'bytes',
              code: cause.code,
              retryable: cause.retryable,
            },
            cause,
          ),
        ),
      ),
  });
  return Layer.mergeAll(
    Layer.succeed(PageSource, pageSource),
    Layer.succeed(PdfTextLayer, textLayer),
    Layer.succeed(ParserSecrets, secrets),
    Layer.succeed(DocumentSource, documentSource),
    Layer.succeed(ParserTracer, parserTracerNoopService),
  );
}

/** Recorded-response HTTP double, composed explicitly with parserHostMemory in tests. */
export function parserHttpLayerMemory(
  fetchDouble: (input: string | URL, init?: RequestInit) => Response | Promise<Response>,
): Layer.Layer<ParserHttp> {
  return Layer.succeed(
    ParserHttp,
    ParserHttp.of({
      fetch: (input, init) =>
        Effect.tryPromise({
          try: () => Promise.resolve(fetchDouble(input, init)),
          catch: (cause) =>
            makeParserDependencyError(
              {
                service: 'http',
                operation: 'fetch',
                code: 'memory_http_double_failed',
                retryable: false,
              },
              cause,
            ),
        }),
    }),
  );
}
